/**
 * --------------------------------------------------------------------------
 * Bootstrap (v4.6.2): modal.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
 * --------------------------------------------------------------------------
 */

import Util from '../BootstrapUtil/bootstrap-util'

/**
 * Constants
 */

const NAME = 'modal'
const VERSION = '4.6.2'
const DATA_KEY = 'bs.modal'
const EVENT_KEY = `.${DATA_KEY}`
const DATA_API_KEY = '.data-api'
const ESCAPE_KEYCODE = 27 // KeyboardEvent.which value for Escape (Esc) key

const CLASS_NAME_SCROLLABLE = 'modal-dialog-scrollable'
const CLASS_NAME_SCROLLBAR_MEASURER = 'modal-scrollbar-measure'
const CLASS_NAME_BACKDROP = 'modal-backdrop'
const CLASS_NAME_OPEN = 'modal-open'
const CLASS_NAME_FADE = 'fade'
const CLASS_NAME_SHOW = 'show'
const CLASS_NAME_STATIC = 'modal-static'

const EVENT_HIDE = `hide${EVENT_KEY}`
const EVENT_HIDE_PREVENTED = `hidePrevented${EVENT_KEY}`
const EVENT_HIDDEN = `hidden${EVENT_KEY}`
const EVENT_SHOW = `show${EVENT_KEY}`
const EVENT_SHOWN = `shown${EVENT_KEY}`
const EVENT_FOCUSIN = `focusin${EVENT_KEY}`
const EVENT_RESIZE = `resize${EVENT_KEY}`
const EVENT_CLICK_DISMISS = `click.dismiss${EVENT_KEY}`
const EVENT_KEYDOWN_DISMISS = `keydown.dismiss${EVENT_KEY}`
const EVENT_MOUSEUP_DISMISS = `mouseup.dismiss${EVENT_KEY}`
const EVENT_MOUSEDOWN_DISMISS = `mousedown.dismiss${EVENT_KEY}`
const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`
const TRANSITION_END = 'transitionend'

const NATIVE_EVENT_FOCUSIN = 'focusin'
const NATIVE_EVENT_RESIZE = 'resize'
const NATIVE_EVENT_KEYDOWN = 'keydown'
const NATIVE_EVENT_CLICK = 'click'
const NATIVE_EVENT_MOUSEDOWN = 'mousedown'
const NATIVE_EVENT_MOUSEUP = 'mouseup'

const SELECTOR_DIALOG = '.modal-dialog'
const SELECTOR_MODAL_BODY = '.modal-body'
const SELECTOR_DATA_TOGGLE = '[data-toggle="modal"]'
const SELECTOR_DATA_DISMISS = '[data-dismiss="modal"]'
const SELECTOR_FIXED_CONTENT = '.fixed-top, .fixed-bottom, .is-fixed, .sticky-top'
const SELECTOR_STICKY_CONTENT = '.sticky-top'

const Default = {
  backdrop: true,
  keyboard: true,
  focus: true,
  show: true
}

const DefaultType = {
  backdrop: '(boolean|string)',
  keyboard: 'boolean',
  focus: 'boolean',
  show: 'boolean'
}

/**
 * Class definition
 */

class Modal {
  static _instances = new WeakMap()

  constructor(element, config) {
    this._config = this._getConfig(config)
    this._element = element
    this._dialog = element.querySelector(SELECTOR_DIALOG)
    this._backdrop = null
    this._isShown = false
    this._isBodyOverflowing = false
    this._ignoreBackdropClick = false
    this._isTransitioning = false
    this._scrollbarWidth = 0
    this._scrollbarPaddingMap = new Map()
    this._scrollbarMarginMap = new Map()
    this._bodyOriginalPaddingRight = ''
    this._focusinHandler = null
    this._keydownDismissHandler = null
    this._resizeHandler = null
    this._dismissClickHandler = null
    this._dialogMouseDownHandler = null
    this._dialogMouseUpHandler = null
    this._backdropClickHandler = null

    Modal._instances.set(this._element, this)
  }

  // Getters
  static get VERSION() {
    return VERSION
  }

  static get Default() {
    return Default
  }

  // Public
  toggle(relatedTarget) {
    return this._isShown ? this.hide() : this.show(relatedTarget)
  }

  show(relatedTarget) {
    if (this._isShown || this._isTransitioning) {
      return
    }

    const showEvent = Modal._triggerEvent(this._element, EVENT_SHOW, { relatedTarget })
    if (showEvent.defaultPrevented) {
      return
    }

    this._isShown = true

    if (this._element.classList.contains(CLASS_NAME_FADE)) {
      this._isTransitioning = true
    }

    this._checkScrollbar()
    this._setScrollbar()

    this._adjustDialog()

    this._setEscapeEvent()
    this._setResizeEvent()

    this._dismissClickHandler = event => {
      const dismissTarget = event.target.closest(SELECTOR_DATA_DISMISS)
      if (dismissTarget && this._element.contains(dismissTarget)) {
        this.hide(event)
      }
    }
    this._element.addEventListener(NATIVE_EVENT_CLICK, this._dismissClickHandler)

    this._dialogMouseDownHandler = () => {
      this._dialogMouseUpHandler = event => {
        if (event.target === this._element) {
          this._ignoreBackdropClick = true
        }
        this._element.removeEventListener(NATIVE_EVENT_MOUSEUP, this._dialogMouseUpHandler)
        this._dialogMouseUpHandler = null
      }
      this._element.addEventListener(NATIVE_EVENT_MOUSEUP, this._dialogMouseUpHandler)
    }
    this._dialog.addEventListener(NATIVE_EVENT_MOUSEDOWN, this._dialogMouseDownHandler)

    this._showBackdrop(() => this._showElement(relatedTarget))
  }

  hide(event) {
    if (event) {
      event.preventDefault()
    }

    if (!this._isShown || this._isTransitioning) {
      return
    }

    const hideEvent = Modal._triggerEvent(this._element, EVENT_HIDE)
    if (!this._isShown || hideEvent.defaultPrevented) {
      return
    }

    this._isShown = false
    const transition = this._element.classList.contains(CLASS_NAME_FADE)

    if (transition) {
      this._isTransitioning = true
    }

    this._setEscapeEvent()
    this._setResizeEvent()

    this._removeFocusinHandler()

    this._element.classList.remove(CLASS_NAME_SHOW)

    if (this._dismissClickHandler) {
      this._element.removeEventListener(NATIVE_EVENT_CLICK, this._dismissClickHandler)
      this._dismissClickHandler = null
    }
    if (this._dialogMouseDownHandler) {
      this._dialog.removeEventListener(NATIVE_EVENT_MOUSEDOWN, this._dialogMouseDownHandler)
      this._dialogMouseDownHandler = null
    }
    if (this._dialogMouseUpHandler) {
      this._element.removeEventListener(NATIVE_EVENT_MOUSEUP, this._dialogMouseUpHandler)
      this._dialogMouseUpHandler = null
    }
    if (this._backdropClickHandler) {
      this._element.removeEventListener(NATIVE_EVENT_CLICK, this._backdropClickHandler)
      this._backdropClickHandler = null
    }

    if (transition) {
      const transitionDuration = Util.getTransitionDurationFromElement(this._element)
      Modal._queueCallback(() => this._hideModal(), this._element, transitionDuration)
    } else {
      this._hideModal()
    }
  }

  dispose() {
    this._removeFocusinHandler()
    if (this._keydownDismissHandler) {
      this._element.removeEventListener(NATIVE_EVENT_KEYDOWN, this._keydownDismissHandler)
      this._keydownDismissHandler = null
    }
    if (this._resizeHandler) {
      window.removeEventListener(NATIVE_EVENT_RESIZE, this._resizeHandler)
      this._resizeHandler = null
    }
    if (this._dismissClickHandler) {
      this._element.removeEventListener(NATIVE_EVENT_CLICK, this._dismissClickHandler)
      this._dismissClickHandler = null
    }
    if (this._dialogMouseDownHandler) {
      this._dialog.removeEventListener(NATIVE_EVENT_MOUSEDOWN, this._dialogMouseDownHandler)
      this._dialogMouseDownHandler = null
    }
    if (this._dialogMouseUpHandler) {
      this._element.removeEventListener(NATIVE_EVENT_MOUSEUP, this._dialogMouseUpHandler)
      this._dialogMouseUpHandler = null
    }
    if (this._backdropClickHandler) {
      this._element.removeEventListener(NATIVE_EVENT_CLICK, this._backdropClickHandler)
      this._backdropClickHandler = null
    }

    Modal._instances.delete(this._element)

    this._config = null
    this._element = null
    this._dialog = null
    this._backdrop = null
    this._isShown = null
    this._isBodyOverflowing = null
    this._ignoreBackdropClick = null
    this._isTransitioning = null
    this._scrollbarWidth = null
  }

  handleUpdate() {
    this._adjustDialog()
  }

  // Private
  _getConfig(config) {
    config = {
      ...Default,
      ...config
    }
    Util.typeCheckConfig(NAME, config, DefaultType)
    return config
  }

  _triggerBackdropTransition() {
    const hideEventPrevented = Modal._triggerEvent(this._element, EVENT_HIDE_PREVENTED)
    if (hideEventPrevented.defaultPrevented) {
      return
    }

    const isModalOverflowing = this._element.scrollHeight > document.documentElement.clientHeight

    if (!isModalOverflowing) {
      this._element.style.overflowY = 'hidden'
    }

    this._element.classList.add(CLASS_NAME_STATIC)

    const modalTransitionDuration = Util.getTransitionDurationFromElement(this._dialog)
    Modal._queueCallback(() => {
      this._element.classList.remove(CLASS_NAME_STATIC)
      if (!isModalOverflowing) {
        Modal._queueCallback(() => {
          this._element.style.overflowY = ''
        }, this._element, modalTransitionDuration)
      }
    }, this._element, modalTransitionDuration)

    this._element.focus()
  }

  _showElement(relatedTarget) {
    const transition = this._element.classList.contains(CLASS_NAME_FADE)
    const modalBody = this._dialog ? this._dialog.querySelector(SELECTOR_MODAL_BODY) : null

    if (!this._element.parentNode ||
        this._element.parentNode.nodeType !== Node.ELEMENT_NODE) {
      // Don't move modal's DOM position
      document.body.appendChild(this._element)
    }

    this._element.style.display = 'block'
    this._element.inert = false
    this._element.removeAttribute('inert')
    this._element.removeAttribute('aria-hidden')
    this._element.setAttribute('aria-modal', true)
    this._element.setAttribute('role', 'dialog')

    if (this._dialog.classList.contains(CLASS_NAME_SCROLLABLE) && modalBody) {
      modalBody.scrollTop = 0
    } else {
      this._element.scrollTop = 0
    }

    if (transition) {
      Util.reflow(this._element)
    }

    this._element.classList.add(CLASS_NAME_SHOW)

    if (this._config.focus) {
      this._enforceFocus()
    }

    const transitionComplete = () => {
      if (this._config.focus) {
        this._element.focus()
      }

      this._isTransitioning = false
      Modal._triggerEvent(this._element, EVENT_SHOWN, { relatedTarget })
    }

    if (transition) {
      const transitionDuration = Util.getTransitionDurationFromElement(this._dialog)
      Modal._queueCallback(transitionComplete, this._dialog, transitionDuration)
    } else {
      transitionComplete()
    }
  }

  _enforceFocus() {
    this._removeFocusinHandler()
    this._focusinHandler = event => {
      if (
        document !== event.target &&
        this._element !== event.target &&
        !this._element.contains(event.target)
      ) {
        this._element.focus()
      }
    }
    document.addEventListener(NATIVE_EVENT_FOCUSIN, this._focusinHandler)
  }

  _removeFocusinHandler() {
    if (!this._focusinHandler) {
      return
    }
    document.removeEventListener(NATIVE_EVENT_FOCUSIN, this._focusinHandler)
    this._focusinHandler = null
  }

  _setEscapeEvent() {
    if (this._isShown) {
      if (this._keydownDismissHandler) {
        this._element.removeEventListener(NATIVE_EVENT_KEYDOWN, this._keydownDismissHandler)
      }
      this._keydownDismissHandler = event => {
        if (this._config.keyboard && event.keyCode === ESCAPE_KEYCODE) {
          event.preventDefault()
          this.hide()
        } else if (!this._config.keyboard && event.keyCode === ESCAPE_KEYCODE) {
          this._triggerBackdropTransition()
        }
      }
      this._element.addEventListener(NATIVE_EVENT_KEYDOWN, this._keydownDismissHandler)
    } else if (!this._isShown) {
      if (this._keydownDismissHandler) {
        this._element.removeEventListener(NATIVE_EVENT_KEYDOWN, this._keydownDismissHandler)
        this._keydownDismissHandler = null
      }
    }
  }

  _setResizeEvent() {
    if (this._isShown) {
      if (this._resizeHandler) {
        window.removeEventListener(NATIVE_EVENT_RESIZE, this._resizeHandler)
      }
      this._resizeHandler = event => this.handleUpdate(event)
      window.addEventListener(NATIVE_EVENT_RESIZE, this._resizeHandler)
    } else {
      if (this._resizeHandler) {
        window.removeEventListener(NATIVE_EVENT_RESIZE, this._resizeHandler)
        this._resizeHandler = null
      }
    }
  }

  _hideModal() {
    const active = document.activeElement
    if (active && (active === this._element || this._element.contains(active))) {
      active.blur()
    }

    this._element.style.display = 'none'
    this._element.inert = true
    this._element.setAttribute('aria-hidden', true)
    this._element.removeAttribute('aria-modal')
    this._element.removeAttribute('role')
    this._isTransitioning = false
    this._showBackdrop(() => {
      document.body.classList.remove(CLASS_NAME_OPEN)
      this._resetAdjustments()
      this._resetScrollbar()
      Modal._triggerEvent(this._element, EVENT_HIDDEN)
    })
  }

  _removeBackdrop() {
    if (this._backdrop) {
      this._backdrop.remove()
      this._backdrop = null
    }
  }

  _showBackdrop(callback) {
    const animate = this._element.classList.contains(CLASS_NAME_FADE) ?
      CLASS_NAME_FADE : ''

    if (this._isShown && this._config.backdrop) {
      this._backdrop = document.createElement('div')
      this._backdrop.className = CLASS_NAME_BACKDROP

      if (animate) {
        this._backdrop.classList.add(animate)
      }

      document.body.appendChild(this._backdrop)

      if (this._backdropClickHandler) {
        this._element.removeEventListener(NATIVE_EVENT_CLICK, this._backdropClickHandler)
      }
      this._backdropClickHandler = event => {
        if (this._ignoreBackdropClick) {
          this._ignoreBackdropClick = false
          return
        }

        if (event.target !== event.currentTarget) {
          return
        }

        if (this._config.backdrop === 'static') {
          this._triggerBackdropTransition()
        } else {
          this.hide()
        }
      }
      this._element.addEventListener(NATIVE_EVENT_CLICK, this._backdropClickHandler)

      if (animate) {
        Util.reflow(this._backdrop)
      }

      this._backdrop.classList.add(CLASS_NAME_SHOW)

      if (!callback) {
        return
      }

      if (!animate) {
        callback()
        return
      }

      const backdropTransitionDuration = Util.getTransitionDurationFromElement(this._backdrop)
      Modal._queueCallback(callback, this._backdrop, backdropTransitionDuration)
    } else if (!this._isShown && this._backdrop) {
      this._backdrop.classList.remove(CLASS_NAME_SHOW)

      const callbackRemove = () => {
        this._removeBackdrop()
        if (callback) {
          callback()
        }
      }

      if (this._element.classList.contains(CLASS_NAME_FADE)) {
        const backdropTransitionDuration = Util.getTransitionDurationFromElement(this._backdrop)
        Modal._queueCallback(callbackRemove, this._backdrop, backdropTransitionDuration)
      } else {
        callbackRemove()
      }
    } else if (callback) {
      callback()
    }
  }

  // ----------------------------------------------------------------------
  // the following methods are used to handle overflowing modals
  // todo (fat): these should probably be refactored out of modal.js
  // ----------------------------------------------------------------------

  _adjustDialog() {
    const isModalOverflowing = this._element.scrollHeight > document.documentElement.clientHeight

    if (!this._isBodyOverflowing && isModalOverflowing) {
      this._element.style.paddingLeft = `${this._scrollbarWidth}px`
    }

    if (this._isBodyOverflowing && !isModalOverflowing) {
      this._element.style.paddingRight = `${this._scrollbarWidth}px`
    }
  }

  _resetAdjustments() {
    this._element.style.paddingLeft = ''
    this._element.style.paddingRight = ''
  }

  _checkScrollbar() {
    const viewportWidth = window.innerWidth
    const documentWidth = document.documentElement.clientWidth
    this._isBodyOverflowing = viewportWidth > documentWidth

    // Fallback for layouts where clientWidth is not reliable.
    if (!this._isBodyOverflowing) {
      const rect = document.body.getBoundingClientRect()
      this._isBodyOverflowing = Math.round(rect.left + rect.right) < viewportWidth
    }

    this._scrollbarWidth = this._getScrollbarWidth()
  }

  _setScrollbar() {
    if (this._isBodyOverflowing) {
      // Note: DOMNode.style.paddingRight returns the actual value or '' if not set
      //   while $(DOMNode).css('padding-right') returns the calculated value or 0 if not set
      const fixedContent = [].slice.call(document.querySelectorAll(SELECTOR_FIXED_CONTENT))
      const stickyContent = [].slice.call(document.querySelectorAll(SELECTOR_STICKY_CONTENT))

      // Adjust fixed content padding
      fixedContent.forEach(element => {
        const actualPadding = element.style.paddingRight
        const calculatedPadding = window.getComputedStyle(element).paddingRight
        this._scrollbarPaddingMap.set(element, actualPadding)
        element.style.paddingRight = `${parseFloat(calculatedPadding) + this._scrollbarWidth}px`
      })

      // Adjust sticky content margin
      stickyContent.forEach(element => {
        const actualMargin = element.style.marginRight
        const calculatedMargin = window.getComputedStyle(element).marginRight
        this._scrollbarMarginMap.set(element, actualMargin)
        element.style.marginRight = `${parseFloat(calculatedMargin) - this._scrollbarWidth}px`
      })

      // Adjust body padding
      this._bodyOriginalPaddingRight = document.body.style.paddingRight
      const calculatedPadding = window.getComputedStyle(document.body).paddingRight
      document.body.style.paddingRight = `${parseFloat(calculatedPadding) + this._scrollbarWidth}px`
    }

    document.body.classList.add(CLASS_NAME_OPEN)
  }

  _resetScrollbar() {
    // Restore fixed content padding
    const fixedContent = [].slice.call(document.querySelectorAll(SELECTOR_FIXED_CONTENT))
    fixedContent.forEach(element => {
      const padding = this._scrollbarPaddingMap.get(element)
      element.style.paddingRight = typeof padding === 'string' ? padding : ''
    })
    this._scrollbarPaddingMap.clear()

    // Restore sticky content
    const elements = [].slice.call(document.querySelectorAll(`${SELECTOR_STICKY_CONTENT}`))
    elements.forEach(element => {
      const margin = this._scrollbarMarginMap.get(element)
      if (typeof margin !== 'undefined') {
        element.style.marginRight = margin
      }
    })
    this._scrollbarMarginMap.clear()

    // Restore body padding
    document.body.style.paddingRight = this._bodyOriginalPaddingRight || ''
    this._bodyOriginalPaddingRight = ''
  }

  _getScrollbarWidth() { // thx d.walsh
    const scrollDiv = document.createElement('div')
    scrollDiv.className = CLASS_NAME_SCROLLBAR_MEASURER
    document.body.appendChild(scrollDiv)
    const scrollbarWidth = scrollDiv.getBoundingClientRect().width - scrollDiv.clientWidth
    document.body.removeChild(scrollDiv)
    return scrollbarWidth
  }

  // Static
  static _queueCallback(callback, element, transitionDuration) {
    let called = false
    const duration = transitionDuration || 0
    const handler = event => {
      if (event.target !== element) {
        return
      }
      called = true
      element.removeEventListener(TRANSITION_END, handler)
      callback()
    }

    element.addEventListener(TRANSITION_END, handler)

    window.setTimeout(() => {
      if (!called) {
        Util.triggerTransitionEnd(element)
      }
    }, duration + 5)
  }

  static _triggerEvent(element, eventName, extra = {}) {
    const event = new CustomEvent(eventName, {
      bubbles: true,
      cancelable: true,
      detail: extra
    })

    if (typeof extra.relatedTarget !== 'undefined') {
      Object.defineProperty(event, 'relatedTarget', {
        configurable: true,
        enumerable: true,
        value: extra.relatedTarget
      })
    }

    element.dispatchEvent(event)

    const jQueryRef = window.jQuery
    if (typeof jQueryRef !== 'undefined') {
      const jqEvent = jQueryRef.Event(eventName, extra)
      jQueryRef(element).trigger(jqEvent)
      if (jqEvent.isDefaultPrevented()) {
        event.preventDefault()
      }
    }

    return event
  }

  static _getDataAttributes(element) {
    if (!element) {
      return {}
    }

    const data = {}
    if (typeof element.dataset.backdrop !== 'undefined') {
      data.backdrop = element.dataset.backdrop === 'true'
        ? true
        : element.dataset.backdrop === 'false'
          ? false
          : element.dataset.backdrop
    }
    if (typeof element.dataset.keyboard !== 'undefined') {
      data.keyboard = element.dataset.keyboard === 'true'
    }
    if (typeof element.dataset.focus !== 'undefined') {
      data.focus = element.dataset.focus === 'true'
    }
    if (typeof element.dataset.show !== 'undefined') {
      data.show = element.dataset.show === 'true'
    }
    return data
  }

  static getInstance(element) {
    return this._instances.get(element) || null
  }

  static getOrCreateInstance(element, config = {}) {
    return this.getInstance(element) || new this(element, config)
  }

  static interface(elements, config, relatedTarget) {
    elements.forEach(element => {
      let data = Modal.getInstance(element)
      const _config = {
        ...Default,
        ...Modal._getDataAttributes(element),
        ...(typeof config === 'object' && config ? config : {})
      }

      if (!data) {
        data = new Modal(element, _config)
      }

      if (typeof config === 'string') {
        if (typeof data[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`)
        }

        data[config](relatedTarget)
      } else if (_config.show) {
        data.show(relatedTarget)
      }
    })
  }
}

/**
 * Data API implementation
 */

document.addEventListener(NATIVE_EVENT_CLICK, function (event) {
  const trigger = event.target.closest(SELECTOR_DATA_TOGGLE)
  if (!trigger) {
    return
  }

  let target
  const selector = Util.getSelectorFromElement(trigger)

  if (selector) {
    target = document.querySelector(selector)
  }
  if (!target) {
    return
  }

  const config = Modal.getInstance(target) ?
    'toggle' : {
      ...Modal._getDataAttributes(target),
      ...Modal._getDataAttributes(trigger)
    }

  if (trigger.tagName === 'A' || trigger.tagName === 'AREA') {
    event.preventDefault()
  }

  target.addEventListener(EVENT_SHOW, showEvent => {
    if (showEvent.defaultPrevented) {
      // Only register focus restorer if modal will actually get shown
      return
    }

    target.addEventListener(EVENT_HIDDEN, () => {
      const isVisible = trigger.offsetParent !== null || trigger.getClientRects().length > 0
      if (isVisible) {
        trigger.focus()
      }
    }, { once: true })
  }, { once: true })

  Modal.interface([target], config, trigger)
})

/**
 * Optional jQuery bridge
 */
if (typeof window.jQuery !== 'undefined') {
  const $ = window.jQuery
  const JQUERY_NO_CONFLICT = $.fn[NAME]

  $.fn[NAME] = function (config, relatedTarget) {
    return Modal.interface([].slice.call(this), config, relatedTarget)
  }
  $.fn[NAME].Constructor = Modal
  $.fn[NAME].noConflict = () => {
    $.fn[NAME] = JQUERY_NO_CONFLICT
    return $.fn[NAME]
  }
}

export default Modal
